import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Flex } from '@atlaskit/primitives/compiled';
import { CornerStyle, RenderVariant } from '@atlassian/company-hub-common';
import { AlignmentField, CornerStyleConfig } from '@atlassian/company-hub-common/config-panel';

import { CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE } from '../../../../constants';
import { cardNewParametersV4 } from '../../../../controllers/use-cards-extension-parameters-and-actions';
import type { CardsActions } from '../../../../controllers/use-cards-extension-parameters-and-actions/types';
import {
	type CardsExtensionParameters,
	CardSize,
	CardsLayout,
	Orientation,
} from '../../../../types';
import { AutoscrollToggle } from '../../fields/autoscroll-toggle';
import { BackgroundColorField } from '../../fields/background-color-field';
import { BorderColorField } from '../../fields/border-color-field';
import { OrientationField } from '../../fields/orientation-field';
import { PaddingToggle } from '../../fields/padding-toggle';
import { SizeField } from '../../fields/size-field';
import { TextColorField } from '../../fields/text-color-field';

const styles = cssMap({
	disabledContentWrapperStyles: {
		cursor: 'not-allowed',
	},
});

type CardsConfigStylesSectionGeneralProps = {
	extensionActions: CardsActions;
	extensionParameters: CardsExtensionParameters;
	isCompanyHub: boolean;
	isDisabled: boolean;
};

export const CardsConfigStylesSectionGeneral = ({
	extensionParameters,
	extensionActions,
	isCompanyHub,
	isDisabled,
}: CardsConfigStylesSectionGeneralProps): React.JSX.Element => {
	const {
		size,
		orientation,
		alignment,
		renderVariant,
		isAutoscrollEnabled,
		borderColor,
		isPaddingEnabled,
		cornerStyle,
		layout,
		filters,
		// to account for when backgroundColor is undefined for cards created before visual updates
		backgroundColor = cardNewParametersV4.backgroundColor,
		// to account for when textColor is undefined for cards created before visual updates
		textColor = cardNewParametersV4.textColor,
	} = extensionParameters;

	const { updateMainFormField, updateCornerStyle } = extensionActions;

	const isUnpopulatedDynamicCards = renderVariant === RenderVariant.DYNAMIC && !filters;
	const shouldShowAutoscrollToggle = isCompanyHub && orientation === Orientation.ROW && !isUnpopulatedDynamicCards;

	return (
		<Flex
			testId="styles-general-section"
			direction="column"
			gap="space.200"
			xcss={isDisabled && styles.disabledContentWrapperStyles}
		>
			<SizeField
				sizeParameter={size}
				renderVariant={renderVariant}
				updateMainFormField={updateMainFormField}
				showUpdatedStyles
			/>
			<OrientationField
				orientation={orientation}
				renderVariant={renderVariant}
				updateMainFormField={updateMainFormField}
				showUpdatedStyles
			/>
			{shouldShowAutoscrollToggle && (
				<AutoscrollToggle
					isAutoscrollEnabled={isAutoscrollEnabled}
					renderVariant={renderVariant}
					updateMainFormField={updateMainFormField}
				/>
			)}
			<BackgroundColorField
				textColor={textColor}
				backgroundColor={backgroundColor}
				isCompanyHub={isCompanyHub}
				updateMainFormField={updateMainFormField}
			/>
			<TextColorField
				textColor={textColor}
				backgroundColor={backgroundColor}
				isCompanyHub={isCompanyHub}
				updateMainFormField={updateMainFormField}
			/>
			{layout === CardsLayout.FIXED_IMAGE_HEIGHT && size !== CardSize.SMALL && (
				<>
					<BorderColorField
						borderColor={borderColor}
						isCompanyHub={isCompanyHub}
						updateMainFormField={updateMainFormField}
					/>
					<PaddingToggle
						isPaddingEnabled={isPaddingEnabled}
						renderVariant={renderVariant}
						updateMainFormField={updateMainFormField}
					/>
				</>
			)}
			{isCompanyHub && (
				<CornerStyleConfig<CornerStyle>
					cornerStyleValue={cornerStyle ?? CornerStyle.ROUNDED}
					updateCornerStyle={updateCornerStyle}
					roundCornerValue={CornerStyle.ROUNDED}
					squareCornerValue={CornerStyle.SQUARED}
				/>
			)}
			{orientation === Orientation.STACKED && (
				<AlignmentField
					selectedAlignment={alignment}
					renderVariant={renderVariant}
					updateMainFormField={updateMainFormField}
					source={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
				/>
			)}
		</Flex>
	);
};
